import { Box, Button, Container, Grid, IconButton, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Iconify from "src/components/Iconify";
import Page from "src/components/Page";
import MRTTable from "src/components/mrt-table";
import useSettings from "src/hooks/useSettings";
import useAuth from "src/hooks/useAuth";
import { getAllIncidentTypes, getActiveCompanies } from "src/pages/api";
import AddIncidentCategory from "./AddIncidentCategory";
import DeleteRecord from "src/components/DeleteRecord";

export default function IncidentCategoryMaster() {
  const { themeStretch } = useSettings();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isCompany = user?.role === "COMPANY";

  const { data: companiesData = [] } = useQuery(
    ["companies-active-incident-list"],
    ({ signal }) => getActiveCompanies({ signal }),
    { enabled: isSuperAdmin, staleTime: 5 * 60 * 1000 }
  );

  const companyMap = React.useMemo(() => {
    const map = {};
    companiesData.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [companiesData]);

  const columns = [
    ...(!isCompany
      ? [
          {
            accessorFn: (row) =>
              row.companyId ? (companyMap[row.companyId] || row.companyId) : "Default",
            id: "companyName",
            header: "Company",
            size: 250,
          },
        ]
      : []),
    { accessorKey: "name", header: "Name", size: 250 },
  ];

  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: [
      "incident-types",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter || "",
    ],
    queryFn: getAllIncidentTypes,
    keepPreviousData: true,
    staleTime: 0,
  });

  const canEditRow = (row) => isSuperAdmin || !row.isDefault;

  const handleRowClick = (row) => {
    if (!canEditRow(row.original)) return;
    setOpen(true);
    setSelectedRow(row.original);
    setIsEditMode(true);
  };

  return (
    <>
      {open && (
        <AddIncidentCategory
          onClose={() => {
            setOpen(false);
            setSelectedRow(null);
            setIsEditMode(false);
            refetch();
          }}
          row={selectedRow}
          isEditMode={isEditMode}
        />
      )}
      {openDelete && selectedRow && (
        <DeleteRecord
          row={selectedRow}
          title="Delete Incident Category"
          endpoint="/incidents/types"
          labelKey="name"
          queryKey={["incident-types"]}
          onClose={() => {
            setOpenDelete(false);
            setSelectedRow(null);
            refetch();
          }}
        />
      )}
      <Page title="Incident Category">
        <Container maxWidth={themeStretch ? false : "xl"}>
          <HeaderBreadcrumbs
            heading="Incident Categories"
            links={[
              { name: "Master" },
              { name: "Incident Categories" },
            ]}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ overflowX: "auto", minWidth: 0 }}>
              <MRTTable
                data={data?.data || []}
                columns={columns}
                manualPagination={true}
                loading={isLoading || isFetching}
                onPaginationChange={setPagination}
                pagination={pagination}
                rowCount={data?.total || 0}
                onGlobalFilterChange={(value) => {
                  setGlobalFilter(value ?? "");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleRowClick(row),
                  sx: {
                    cursor: canEditRow(row.original) ? "pointer" : "default",
                    opacity: row.original.isDefault && !isSuperAdmin ? 0.6 : 1,
                    "&:hover": {
                      backgroundColor: canEditRow(row.original)
                        ? "action.hover"
                        : "inherit",
                    },
                  },
                })}
                renderRowActions={({ row }) => {
                  const disabled = !canEditRow(row.original);
                  return (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Tooltip
                        title={disabled ? "Default categories cannot be deleted" : ""}
                      >
                        <span>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDelete(true);
                              setSelectedRow(row.original);
                            }}
                            color="error"
                            size="small"
                            disabled={disabled}
                          >
                            <Iconify icon={"eva:trash-2-outline"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                }}
                renderTopToolbarCustomActions={() => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setOpen(true);
                        setIsEditMode(false);
                      }}
                      sx={{
                        minWidth: "120px",
                        fontSize: "0.875rem",
                        py: 1,
                      }}
                    >
                      Add New
                    </Button>
                  </Box>
                )}
                enableToolbarInternalActions={false}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableSorting={true}
                enableGlobalFilter={true}
                enableHiding={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enablePagination={true}
                positionPagination="bottom"
                enableColumnDragging={false}
                positionGlobalFilter="left"
                muiSearchTextFieldProps={{
                  placeholder: "Search",
                  size: "small",
                  variant: "outlined",
                  value: globalFilter,
                  onChange: (e) => setGlobalFilter(e.target.value),
                }}
                initialState={{
                  showGlobalFilter: true,
                  density: "comfortable",
                  pagination: { pageSize: 10, pageIndex: 0 },
                }}
                state={{
                  globalFilter,
                  pagination,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
